<template>
  <v-dialog v-model="dialogComputed" max-width="800px" scrollable persistent>
    <v-card>
      <v-card-title class="primary white--text py-2">
        {{ dialogTitle }}
        <v-spacer />
        <Btn icon="mdi-close" @click="close" depressed iconProp color="white" />
      </v-card-title>
      <v-card-text>
        <validation-observer v-if="dialogComputed" ref="observer" v-slot="{ invalid }">
          <v-container fluid class="px-0">
            <v-row>
              <v-col>
                <TextFieldRef v-if="isEdit" :label="$t('ELE0031')" :value="partCd" />
                <TextFieldInput
                  v-else-if="!isManagedMoldComputed"
                  :label="$t('ELE0031')"
                  v-model="partCdComputed"
                  maxLength="15"
                  validationMode="lazy"
                  rules="alphaNum|min:9|max:15|partCdUnique|checkTopics"
                  @change="getPtCdList()"
                  required
                />
                <TextFieldInput
                  v-else-if="isManagedMoldComputed"
                  :label="$t('ELE0031')"
                  v-model="partCdComputed"
                  maxLength="13"
                  validationMode="lazy"
                  rules="alphaNum|length:9|checkTopics"
                  @change="getPtCdList()"
                  required
                />
                <TextFieldInput
                  v-else
                  :label="$t('ELE0031')"
                  v-model="partCdComputed"
                  maxLength="13"
                  validationMode="lazy"
                  rules="alphaNum|min:9|max:13|checkTopics"
                  @change="getPtCdList()"
                  required
                />
              </v-col>
              <v-col v-if="isEdit">
                <TextFieldRef
                  class="pr-auto"
                  :label="$t('ELE0032')"
                  v-model="partNameComputed"
                  outerIcon="mdi-reload"
                  @onClickOuterIcon="getTopicsData(partCd)"
                  required
                />
              </v-col>
              <v-col v-else>
                <TextFieldRef class="pr-auto" :label="$t('ELE0032')" v-model="partNameComputed" required />
              </v-col>
            </v-row>
            <v-row v-if="isShownMold">
              <v-col>
                <Checkbox v-if="isEdit" :label="$t('ELE0495')" v-model="isManagedMoldComputed" disabled />
                <Checkbox v-else :label="$t('ELE0495')" v-model="isManagedMoldComputed" :maxLength="80" required />
              </v-col>
              <v-col>
                <TextFieldRef v-if="!isManagedMoldComputed" :label="$t('ELE0051')" />
                <TextFieldRef v-else-if="isEdit" :label="$t('ELE0051')" :value="moldNoComputed" />
                <TextFieldInput
                  v-else
                  :label="$t('ELE0051')"
                  v-model="moldNoComputed"
                  validationMode="lazy"
                  :rules="`alphaNum|min:2|max:2|checkMoldUnique:${partCd}`"
                  maxLength="2"
                  required
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <TextFieldInput :label="$t('ELE0224')" v-model="modelComputed" :maxLength="80" required />
              </v-col>
              <v-col>
                <PartCatInput v-model="partCatCdComputed" validOnly required />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <PartGroupInput v-model="partGroupCdComputed" validOnly required />
              </v-col>
              <v-col>
                <MeasuringInstrumentInput
                  v-model="measInstCdComputed"
                  :isSelectedFirstItem="isAdd"
                  validOnly
                  required
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <PartInspTimingInput
                  v-model="partInspTimingCdComputed"
                  validOnly
                  required
                  @change="onChangePartInspTimingCd"
                />
              </v-col>
              <v-col>
                <PartInspQtyStdInput
                  v-model="inspQtyStdCdComputed"
                  :partInspTimingCd="partInspTimingCdComputed"
                  validOnly
                  required
                  @change="changeInspQtyStd"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col></v-col>
              <v-col v-if="canEditFixedQty">
                <TextFieldInput
                  :label="$t('ELE0285')"
                  v-model="inspBasicQtyComputed"
                  rules="naturalNumberQty"
                  right
                  required
                />
              </v-col>
              <v-col v-else>
                <TextFieldRef :label="$t('ELE0285')" :value="inspBasicQtyComputed" right />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <InspFilteringInput :label="$t('ELE0289')" v-model="oqcInspFiltering" validOnly required />
              </v-col>
              <v-col>
                <Btn
                  class="mt-1"
                  :label="$t('ELE0284')"
                  @click="onClickOqcEditCharacteristic"
                  :disabled="!isEdit || oqcInspFiltering === '' || !characItem"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <InspFilteringInput :label="$t('ELE0290')" v-model="iqcInspFiltering" validOnly required />
              </v-col>
              <v-col>
                <Btn
                  class="mt-1"
                  :label="$t('ELE0284')"
                  @click="onClickIqcEditCharacteristic"
                  :disabled="!isEdit || iqcInspFiltering === '' || !characItem"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <AqlRateInput :label="$t('ELE0338')" v-model="oqcAqlRateCdComputed" required validOnly />
              </v-col>
              <v-col>
                <AqlRateInput :label="$t('ELE0339')" v-model="iqcAqlRateCdComputed" required validOnly />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <DefectGroupInput v-model="defectGroupCdComputed" required validOnly />
              </v-col>
              <v-col>
                <CompanyMultipleInput
                  :label="$t('ELE0134')"
                  v-model="supplierCds"
                  validOnly
                  required
                  :companyCat="companyCat"
                />
              </v-col>
            </v-row>
            <v-row v-if="isEdit">
              <v-col>
                <BtnChooseValid v-model="isValidComputed" />
              </v-col>
            </v-row>
          </v-container>
          <v-divider />
          <v-row>
            <v-col class="mt-2 d-flex flex-row justify-end">
              <Checkbox v-if="canSelectCopyCharac" class="mr-3" :label="$t('ELE0514')" v-model="isCopyCharacs" />
              <BtnSave class="mt-1" @click="onClickSave" :disabled="invalid" />
            </v-col>
          </v-row>
        </validation-observer>
      </v-card-text>
    </v-card>
    <EditCharacteristicDialog
      v-if="editInspFilteringDialog"
      :dialog.sync="editInspFilteringDialog"
      :editCharacItem.sync="editCharacItem"
      :selectInspFiltering="selectInspFiltering"
      @setItems="setEditCharacteristic"
    />
  </v-dialog>
</template>

<script>
import { API } from '@/plugins/api'
import { ValidationObserver, extend } from 'vee-validate'
import { ModalMode, OqcIqcCd, CompanyCatCd, SystemParameterKey } from '@/enum'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import cloneDeep from 'lodash/cloneDeep'
import Checkbox from '@/components/atoms/base/checkbox/Checkbox'
import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import SelectSingleInput from '@/components/atoms/base/select/SelectSingleInput'
import Btn from '@/components/atoms/base/button/Btn'
import BtnSave from '@/components/atoms/button/BtnSave'
import BtnChooseValid from '@/components/atoms/button/BtnChooseValid'
import CompanyMultipleInput from '@/components/atoms/select/CompanyMultipleInput'
import PartCatInput from '@/components/atoms/select/PartCatInput'
import PartGroupInput from '@/components/atoms/select/PartGroupInput'
import MeasuringInstrumentInput from '@/components/atoms/select/MeasuringInstrumentInput'
import AqlRateInput from '@/components/atoms/select/AqlRateInput'
import DefectGroupInput from '@/components/atoms/select/DefectGroupInput'
import InspFilteringInput from '@/components/atoms/select/InspFilteringInput'
import PartInspTimingInput from '@/components/atoms/select/PartInspTimingInput'
import PartInspQtyStdInput from '@/components/atoms/select/PartInspQtyStdInput'
import EditCharacteristicDialog from '@/components/organisms/master/dialog/EditCharacteristicDialog'

export default {
  name: 'PartDialog',
  mixins: [APIUtils, Message, Loading],

  components: {
    ValidationObserver,
    TextFieldInput,
    TextFieldRef,
    SelectSingleInput,
    Btn,
    BtnSave,
    BtnChooseValid,
    Checkbox,
    CompanyMultipleInput,
    PartCatInput,
    PartGroupInput,
    MeasuringInstrumentInput,
    AqlRateInput,
    DefectGroupInput,
    InspFilteringInput,
    PartInspTimingInput,
    PartInspQtyStdInput,
    EditCharacteristicDialog,
  },

  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    partCd: {
      type: String,
      required: true,
    },
    partRev: {
      type: Number,
      required: true,
    },
    partName: {
      type: String,
      required: true,
    },
    model: {
      type: String,
      required: true,
    },
    partCatCd: {
      type: String,
      required: true,
    },
    partGroupCd: {
      type: String,
      required: true,
    },
    measInstCd: {
      type: String,
      required: true,
    },
    partInspTimingCd: {
      type: String,
      required: true,
    },
    inspQtyStdCd: {
      type: String,
      required: true,
    },
    inspBasicQty: {
      type: [Number, String],
      required: true,
      default: 0,
    },
    defectGroupCd: {
      type: String,
      required: true,
    },
    isValid: {
      type: Boolean,
      required: true,
      default: true,
    },
    versionNo: {
      type: Number,
      required: true,
    },
    isShownMold: {
      type: Boolean,
      required: true,
    },
    isManagedMold: {
      type: Boolean,
      required: true,
    },
    moldNo: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      oqcInspFiltering: '',
      iqcInspFiltering: '',
      oqcFilteringList: [],
      iqcFilteringList: [],
      oqcAqlRateCdComputed: '',
      iqcAqlRateCdComputed: '',
      editInspFilteringDialog: false,
      supplierCds: [],
      characItem: null,
      editCharacItem: null,
      selectInspFiltering: '',
      companyCat: CompanyCatCd.SUPPLIER,
      ptCdLen: 9,
      inspFilteringInfo: {
        partCd: '',
        partRev: '',
        inspTimingCd: '',
        inspFilteringGroupCd: '',
      },
      canEditFixedQty: false,
      orgPartCd: '',
      isCopyCharacs: true,
      ptCdList: [],
      settings: [
        {
          mode: ModalMode.ADD,
          title: this.$t('ELE0016'),
          url: '/master/parts/register',
        },
        {
          mode: ModalMode.EDIT,
          title: this.$t('ELE0017'),
          url: '/master/parts/{id}/update',
        },
        {
          mode: ModalMode.COPY,
          title: this.$t('ELE0251'),
          url: '/master/parts/register',
        },
      ],
    }
  },

  computed: {
    dialogTitle: {
      get() {
        let result = this.setting.title
        if (this.isCopy) {
          result = this.$t('ELE0515', { p0: this.orgPartCd })
        }
        return result
      },
    },
    dialogComputed: {
      get() {
        return this.dialog
      },
      set(value) {
        this.$emit('update:dialog', value)
      },
    },
    partCdComputed: {
      get() {
        return this.partCd
      },
      set(value) {
        if (value) {
          value = value.trim()
        }
        this.$emit('update:partCd', value)
      },
    },
    partRevComputed: {
      get() {
        return this.partRev
      },
      set(value) {
        this.$emit('update:partRev', value)
      },
    },
    partNameComputed: {
      get() {
        return this.partName
      },
      set(value) {
        this.$emit('update:partName', value)
      },
    },
    modelComputed: {
      get() {
        return this.model
      },
      set(value) {
        this.$emit('update:model', value)
      },
    },
    partCatCdComputed: {
      get() {
        return this.partCatCd
      },
      set(value) {
        this.$emit('update:partCatCd', value)
      },
    },
    partGroupCdComputed: {
      get() {
        return this.partGroupCd
      },
      set(value) {
        this.$emit('update:partGroupCd', value)
      },
    },
    measInstCdComputed: {
      get() {
        return this.measInstCd
      },
      set(value) {
        this.$emit('update:measInstCd', value)
      },
    },
    partInspTimingCdComputed: {
      get() {
        return this.partInspTimingCd
      },
      set(value) {
        this.$emit('update:partInspTimingCd', value)
      },
    },
    inspQtyStdCdComputed: {
      get() {
        return this.inspQtyStdCd
      },
      set(value) {
        this.$emit('update:inspQtyStdCd', value)
      },
    },
    inspBasicQtyComputed: {
      get() {
        return this.inspBasicQty
      },
      set(value) {
        this.$emit('update:inspBasicQty', value)
      },
    },
    defectGroupCdComputed: {
      get() {
        return this.defectGroupCd
      },
      set(value) {
        this.$emit('update:defectGroupCd', value)
      },
    },
    isValidComputed: {
      get() {
        return this.isValid
      },
      set(value) {
        this.$emit('update:isValid', value)
      },
    },
    setting: {
      get() {
        return this.settings.find((el) => el.mode === this.mode)
      },
    },
    isAdd: {
      get() {
        return this.setting.mode === ModalMode.ADD
      },
    },
    isEdit: {
      get() {
        return this.setting.mode === ModalMode.EDIT
      },
    },
    isCopy: {
      get() {
        return this.setting.mode === ModalMode.COPY
      },
    },
    canSelectCopyCharac: {
      get() {
        // 特性コピーには2パターンあって1が優先
        // 1. 金型違いの部品間で特性コピー
        // 2. 特性コピーチェックボックスを使ったコピー
        //
        // 入力部品コードの先頭9桁で検索して既に金型管理してる部品と判定されたら、
        // 特性コピーチェックボックスを使った処理をさせない
        //
        let result = this.isCopy
        const isManagedMoldExists = this.ptCdList.some((e) => e['isManagedMold'] == true)
        if (this.ptCdList.length >= 1 && (isManagedMoldExists || this.isManagedMoldComputed)) {
          // 入力された部品CDが存在かつ、
          // 既にMold管理されている or これからMold管理を開始する部品の場合False
          result = false
        }
        return result
      },
    },
    isManagedMoldComputed: {
      get() {
        return this.isShownMold && this.isManagedMold
      },
      set(value) {
        this.$emit('update:isManagedMold', value)
      },
    },
    moldNoComputed: {
      get() {
        return this.moldNo
      },
      set(value) {
        this.$emit('update:moldNo', value)
      },
    },
    ptCd: {
      get() {
        if (this.partCdComputed != '' && this.partCdComputed != null && this.partCd.length >= this.ptCdLen) {
          return this.partCdComputed.substr(0, this.ptCdLen)
        } else {
          return this.partCd
        }
      },
    },
  },

  watch: {
    isManagedMoldComputed(newVal) {
      if (!newVal) {
        this.moldNoComputed = ''
      }
    },
    dialog() {
      if (this.dialog) {
        this.init()
      }
    },
  },

  methods: {
    close() {
      this.dialogComputed = false
      this.iqcFilteringList = []
      this.oqcFilteringList = []
      this.characItem = null
      this.oqcAqlRateCdComputed = ''
      this.iqcAqlRateCdComputed = ''
      this.oqcInspFiltering = ''
      this.iqcInspFiltering = ''
      this.canEditFixedQty = false
      this.isCopyCharacs = true
      this.orgPartCd = ''
    },
    async init() {
      this.getInspFilteringCd()
      this.getInspCharFiltering()
      this.getAqlRate()
      this.getSupplierCds()
      if (this.isCopy) {
        this.orgPartCd = cloneDeep(this.partCdComputed)
        this.partCdComputed = ''
        this.partRevComputed = 0
        this.moldNoComputed = ''
      }
    },
    async onClickSave() {
      try {
        if (!(await this.$refs.observer.validate())) {
          return
        }
        const filteringList = this.oqcFilteringList.concat(this.iqcFilteringList)
        this.startLoading()
        const body = {
          partCd: this.partCdComputed,
          partRev: this.partRev + 1,
          partName: this.partNameComputed,
          ptCd: this.ptCd,
          isManagedMold: this.isManagedMoldComputed,
          moldNo: this.moldNoComputed,
          model: this.modelComputed,
          partCatCd: this.partCatCdComputed,
          partGroupCd: this.partGroupCdComputed,
          measInstCd: this.measInstCdComputed,
          partInspTimingCd: this.partInspTimingCdComputed,
          inspQtyStdCd: this.inspQtyStdCdComputed,
          inspBasicQty: this.inspBasicQtyComputed === '' ? null : this.inspBasicQtyComputed,
          oqcAqlRateCd: this.oqcAqlRateCdComputed,
          iqcAqlRateCd: this.iqcAqlRateCdComputed,
          defectGroupCd: this.defectGroupCdComputed,
          supplierCds: this.supplierCds,
          oqcInspFiltering: this.oqcInspFiltering,
          iqcInspFiltering: this.iqcInspFiltering,
          characItem: this.characItem,
          filteringList: filteringList,
          isValid: this.isValidComputed,
          versionNo: this.versionNo,
          orgPartCd: this.orgPartCd,
          isCopyCharacs: this.canSelectCopyCharac ? this.isCopyCharacs : false,
        }
        const url = this.setting.url.replace('{id}', this.partCd)
        const msg = await API.post(url, body)
        this.setSuccessMessage(msg)
        this.close()
        this.$emit('change')
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0003', { p0: this.$t('ELE0059') })))
      } finally {
        this.stopLoading()
      }
    },
    async getSupplierCds() {
      this.supplierCds = []
      try {
        if (this.partCd == '') {
          return
        }
        const body = {
          partCd: this.partCd,
          validOnly: true,
        }
        const elements = await API.get('/master/supplierParts', body)
        elements.forEach((element) => {
          this.supplierCds.push(element['supplierCd'])
        })
        if (!this.isAdd) {
          await setImmediate(() => this.$refs.observer.validate())
        }
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0134') })))
      }
    },
    async getInspFilteringCd() {
      try {
        if (this.isAdd) {
          return
        }
        const body = {
          partCd: this.partCd,
          partRev: this.partRev,
        }
        const elements = await API.get('/master/partInspFilterings', body)
        if (elements.length !== 2) {
          return
        }
        elements.forEach((element) => {
          if (element['inspTimingCd'] === OqcIqcCd.OQC) {
            this.oqcInspFiltering = element['inspFilteringGroupCd']
          } else {
            this.iqcInspFiltering = element['inspFilteringGroupCd']
          }
        })
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0143') })))
      }
    },
    async getTopicsData(partCd) {
      this.startLoading()
      try {
        this.partNameComputed = ''
        const elements = await API.get(`/topics/parts/${partCd.substr(0, 7)}`)
        this.partNameComputed = elements['descriptione']
      } catch (error) {
        if (error.response.status != 404) {
          this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0260') })))
        }
      } finally {
        this.stopLoading()
      }
    },
    async getAqlRate() {
      try {
        if (this.isAdd) {
          return
        }
        const body = {
          partCd: this.partCd,
          partRev: this.partRev,
        }
        const elements = await API.get('/master/partAqlRates', body)
        if (elements.length !== 2) {
          return
        }
        elements.forEach((element) => {
          if (element['inspTimingCd'] === OqcIqcCd.OQC) {
            this.oqcAqlRateCdComputed = element['aqlRateCd']
          } else {
            this.iqcAqlRateCdComputed = element['aqlRateCd']
          }
        })
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0143') })))
      }
    },
    async onClickOqcEditCharacteristic() {
      this.editCharacItem = cloneDeep(this.characItem)
      this.selectInspFiltering = await this.getInspFilteringMap(this.oqcInspFiltering)
      this.oqcFilteringList = this.selectInspFiltering
      this.editInspFilteringDialog = true
    },
    async onClickIqcEditCharacteristic() {
      this.editCharacItem = cloneDeep(this.characItem)
      this.selectInspFiltering = await this.getInspFilteringMap(this.iqcInspFiltering)
      this.iqcFilteringList = this.selectInspFiltering
      this.editInspFilteringDialog = true
    },
    async getInspFilteringMap(filteringGroupCd) {
      this.startLoading()
      try {
        if (filteringGroupCd === '') {
          return
        }
        const params = {
          filteringGroupCd: filteringGroupCd,
        }
        return await API.get(`/master/inspFilterings/group`, params)
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0143') })))
      } finally {
        this.stopLoading()
      }
    },
    async getInspCharFiltering() {
      try {
        this.startLoading()
        const params = {
          partCd: this.partCd,
        }
        this.characItem = await API.get('/master/inspCharFilterings', params)
        this.convertInspFiltering()
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0143') })))
      } finally {
        this.stopLoading()
      }
    },
    async setParameter() {
      try {
        const element = await API.get(`/master/systemParameter/${SystemParameterKey.PT_CD_LEN}`)
        if (element) {
          this.ptCdLen = Number(element.value)
        }
      } catch (error) {
        if (error.response.status != 404) {
          this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0348') })))
        }
      }
    },
    convertInspFiltering() {
      for (let i = 0; i < this.characItem.bodies.length; i++) {
        const charac = this.characItem.bodies[i]
        for (let j = 0; j <= this.characItem.filteringHeader.length - 1; j++) {
          let obj = charac.filtering.find(
            (item) => item.filteringCd == this.characItem.filteringHeader[j].inspFilteringCd,
          )
          if (obj) {
            charac[`${this.characItem.filteringHeader[j].inspFilteringCd}`] = obj.isInsp
          } else {
            charac[`${this.characItem.filteringHeader[j].inspFilteringCd}`] = false
            charac.filtering.push({
              filteringCd: this.characItem.filteringHeader[j].inspFilteringCd,
              isInsp: false,
            })
          }
        }
      }
    },
    setEditCharacteristic() {
      this.characItem = cloneDeep(this.editCharacItem)
    },
    onChangePartInspTimingCd() {
      this.inspQtyStdCdComputed = ''
      this.changeInspQtyStd('', false)
    },
    changeInspQtyStd(value, reqInspBasicQty) {
      if (!reqInspBasicQty) {
        this.inspBasicQtyComputed = ''
      }
      this.canEditFixedQty = reqInspBasicQty
    },
    async getPtCdList() {
      try {
        this.startLoading()
        if (!(this.isCopy && this.ptCd.length >= this.ptCdLen)) return
        this.ptCdList = await API.get('/master/pts', { ptCd: this.ptCd })
      } finally {
        this.stopLoading()
      }
    },
  },

  async mounted() {
    this.setParameter()
    extend('checkTopics', {
      validate: async (value) => {
        let result = false
        if (value === '') {
          result = true
        } else {
          await this.getTopicsData(value)
          if (this.partNameComputed == '') {
            result = this.$t('MSG0091', { p0: value })
          } else {
            result = true
          }
        }
        return result
      },
    })

    this.init()
  },
}
</script>
